/**
 * Untere Navigation.
 *
 * Unten und nicht oben, weil der Daumen die obere Ecke eines heutigen Telefons
 * nicht erreicht (ADR 0001). Die Leiste liegt über dem Geräte-Sicherheitsabstand,
 * damit die ein- und ausfahrende Leiste mobiler Browser nichts abschneidet.
 *
 * Der Eintrag „Meldung" sitzt in der Mitte und ist hervorgehoben: Er ist die
 * häufigste Handlung vor Ort und muss nach ADR 0001 auf jedem Bildschirm
 * erreichbar sein.
 */
import { Link } from "@tanstack/react-router";

import { t } from "@/i18n";
import { cn } from "@/lib/cn";

import { MOBILE_NAV, type NavEntry } from "./navigation";

const ITEM_BASE = cn(
  "flex min-h-touch flex-1 flex-col items-center justify-center gap-0.5 px-0.5 pt-1.5 pb-1",
  "text-[0.6875rem] font-medium leading-tight",
  "[-webkit-tap-highlight-color:transparent]",
);

function itemLabel(entry: NavEntry) {
  return (
    <>
      <entry.icon className="size-6" />
      <span className="max-w-full truncate">{entry.label}</span>
    </>
  );
}

export interface BottomNavProps {
  onOpenMore: () => void;
}

export function BottomNav({ onOpenMore }: BottomNavProps) {
  return (
    <nav
      aria-label={t.nav.ariaBottom}
      className={cn(
        "sticky bottom-0 z-30 shrink-0 border-t border-line bg-surface-raised md:hidden",
        "pb-safe-b",
      )}
    >
      <ul className="flex items-stretch">
        {MOBILE_NAV.map((entry) => {
          const isReport = entry.id === "report";

          // Der einzige Eintrag ohne Ziel ist „Mehr" — er öffnet die Übersicht
          // der übrigen Bereiche. Daneben stand ein Zweig für Bereiche ohne
          // Ansicht; den gibt es in dieser Liste nicht mehr, und ein
          // Erklärungsfenster für einen Fall, der nicht eintritt, ist kein
          // Hinweis, sondern Ballast.
          if (entry.to === null) {
            return (
              <li key={entry.id} className="flex flex-1">
                <button
                  type="button"
                  onClick={onOpenMore}
                  className={cn(ITEM_BASE, "text-ink-muted")}
                >
                  {itemLabel(entry)}
                </button>
              </li>
            );
          }

          return (
            <li key={entry.id} className="flex flex-1">
              <Link
                to={entry.to}
                // Genaue Übereinstimmung nur für die Startseite: Sonst wäre „Start"
                // auf jeder Unterseite mitmarkiert, weil „/" jeden Pfad anführt.
                activeOptions={{ exact: entry.to === "/" }}
                className={cn(
                  ITEM_BASE,
                  "text-ink-muted",
                  // Aktiver Zustand doppelt gekennzeichnet — Farbe und
                  // Balken oben. Farbe allein wäre nach Kapitel 44 zu wenig.
                  "data-[status=active]:relative data-[status=active]:font-semibold data-[status=active]:text-ink",
                  "data-[status=active]:before:absolute data-[status=active]:before:inset-x-3",
                  "data-[status=active]:before:top-0 data-[status=active]:before:h-0.5",
                  "data-[status=active]:before:rounded-full data-[status=active]:before:bg-accent",
                  isReport && "text-ink",
                )}
              >
                {isReport ? (
                  <>
                    <span className="flex size-7 items-center justify-center rounded-full bg-ink text-ink-inverted">
                      <entry.icon className="size-5" />
                    </span>
                    <span className="max-w-full truncate">{entry.label}</span>
                  </>
                ) : (
                  itemLabel(entry)
                )}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
